<template>
    <div>
        <div v-if="userLoading || pageLoading" class="cx-loader-overlay">
            <div class="cx-loader-card">
                <div class="cx-loader-pulse">
                    <div class="cx-loader-pulse-dot"></div>
                </div>
                <p class="cx-loader-title">Loading your workspace</p>
                <p class="cx-loader-hint">{{ hints[hintIndex] }}</p>
                <div class="cx-loader-progress">
                    <div class="cx-loader-progress-bar"></div>
                </div>
            </div>
        </div>
        <v-navigation-drawer v-if="!userLoading && !pageLoading" class="gradient-bg" app clipped v-model="drawer">
            <div class="org-brand">
                <div v-if="orgBranding.logo" class="org-brand-logo-wrap">
                    <img
                        :src="orgBranding.logo"
                        :alt="orgBranding.name || 'Organization logo'"
                        class="org-brand-logo"
                    />
                </div>
                <div v-else class="org-brand-initial">
                    {{ (orgBranding.name || 'D').charAt(0).toUpperCase() }}
                </div>
                <span class="org-brand-name">{{ orgBranding.name || 'DeepTrack' }}</span>
            </div>
            <v-divider></v-divider>
            <v-list dense nav >
                <template v-for="item in items">
          <v-list-group
            v-if="item.items && canAccessGroup(item)"
            :key="item.title"
            v-model="item.active"
            :prepend-icon="item.icon"
            no-action
          >
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title v-text="item.title"></v-list-item-title>
              </v-list-item-content>
            </template>

            <template v-for="child in item.items">
              <v-list-item
                v-if="canAccess(child.name)"
                :key="child.title"
                :to="child.to"
              >
                <v-list-item-content>
                  <v-list-item-title v-text="child.title"></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list-group>

          <v-list-item
            v-if="!item.items && canAccess(item.name)"
            :key="item.title"
            :to="item.to"
          >
            <v-list-item-icon>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
            </v-list>
            <template v-slot:append>
                <v-divider></v-divider>
                <v-menu v-model="profileMenuOpen" top offset-y :nudge-top="8" :close-on-content-click="true" min-width="220">
                    <template v-slot:activator="{ on, attrs }">
                        <div class="sidebar-profile-trigger pa-2 ma-1" v-bind="attrs" v-on="on">
                            <v-avatar size="36" class="sidebar-profile-avatar">
                                <span class="sidebar-profile-initial">{{ user.name ? user.name.charAt(0).toUpperCase() : 'U' }}</span>
                            </v-avatar>
                            <div class="sidebar-profile-info ml-2">
                                <div class="sidebar-profile-name">{{ user.name }}</div>
                                <div class="sidebar-profile-role">{{ user.role || 'User' }}</div>
                            </div>
                            <v-icon small class="ml-auto sidebar-profile-chevron" :class="{ 'rotated': profileMenuOpen }">mdi-chevron-up</v-icon>
                        </div>
                    </template>
                    <v-list dense class="profile-dropdown-list pa-1" style="background: #fff !important; border: 1px solid #E4E7EC; border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.12)">
                        <v-list-item class="profile-dropdown-header" disabled style="background: #fff !important">
                            <v-list-item-content>
                                <v-list-item-title class="profile-dropdown-name">{{ user.name }}</v-list-item-title>
                                <v-list-item-subtitle class="profile-dropdown-email">{{ user.email }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="my-1"></v-divider>
                        <v-list-item @click="dialog = true" class="profile-dropdown-item">
                            <v-list-item-icon class="mr-2"><v-icon small>mdi-account-circle-outline</v-icon></v-list-item-icon>
                            <v-list-item-content><v-list-item-title>My Profile</v-list-item-title></v-list-item-content>
                        </v-list-item>
                        <v-divider class="my-1"></v-divider>
                        <v-list-item @click="logout" class="profile-dropdown-item profile-signout-item">
                            <v-list-item-icon class="mr-2"><v-icon small color="#DC2626">mdi-logout</v-icon></v-list-item-icon>
                            <v-list-item-content><v-list-item-title style="color: #DC2626">Sign Out</v-list-item-title></v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>
        </v-navigation-drawer>
        <v-app-bar v-if="!userLoading" color="primary" dark app clipped-left :height="64">
            <div class="app-branding">
                <div class="app-logo-wrap">
                    <img src="../assets/logo.svg" alt="DeepTrack" class="app-logo-img" />
                </div>
                <span class="app-bar-title">DeepTrack</span>
            </div>

            <v-btn
                outlined
                small
                class="ml-4 text-none header-back-btn"
                @click="goToDashboard"
                title="Back to main dashboard"
            >
                <v-icon left small>mdi-arrow-left</v-icon>
                CCorex
            </v-btn>

            <v-spacer></v-spacer>

            <!-- Dev-only user switcher: become any user without a password -->
            <div v-if="isDev" class="dt-dev-switcher mr-3" title="Dev: switch user (no password)">
                <span class="dt-dev-switcher-label">🧪 Test as:</span>
                <select class="dt-dev-switcher-select" :value="user && user.email" @change="switchUser($event.target.value)">
                    <option v-for="u in devUsers" :key="u.id" :value="u.email">
                        {{ u.name }} — {{ roleSummary(u) }}
                    </option>
                </select>
            </div>

        </v-app-bar>
        <v-main>
            <v-alert class="mt-5" :value="alert.alert" :type="alert.type">{{alert.message}}</v-alert>
            <router-view class="mt-0" style="height: auto;" />
            <v-row>
                <v-dialog v-model="dialog" max-width="480" :overlay-opacity="0.4">
                    <v-card class="profile-dialog-card" style="background:#fff;border-radius:12px;overflow:hidden">
                        <!-- Header -->
                        <div class="profile-dialog-header">
                            <div class="profile-dialog-avatar">
                                {{ user.name ? user.name.charAt(0).toUpperCase() : 'U' }}
                            </div>
                            <div class="profile-dialog-header-info">
                                <div class="profile-dialog-username">{{ user.name }}</div>
                                <div class="profile-dialog-useremail">{{ user.email }}</div>
                            </div>
                            <v-btn icon small class="profile-dialog-close" @click="dialog = false" style="color:rgba(255,255,255,0.8)">
                                <v-icon small>mdi-close</v-icon>
                            </v-btn>
                        </div>

                        <!-- Body -->
                        <v-card-text class="profile-dialog-body" style="background:#fff">
                            <v-form ref="profileForm">
                                <!-- Profile Info Section -->
                                <div class="profile-section-label">Profile Information</div>
                                <v-text-field
                                    label="Full Name"
                                    outlined
                                    dense
                                    prepend-inner-icon="mdi-account-outline"
                                    :rules="req"
                                    v-model="name"
                                    class="profile-field mt-2"
                                    color="#005D6C"
                                ></v-text-field>
                                <v-text-field
                                    disabled
                                    label="Email Address"
                                    outlined
                                    dense
                                    prepend-inner-icon="mdi-email-outline"
                                    readonly
                                    v-model="email"
                                    class="profile-field"
                                    color="#005D6C"
                                ></v-text-field>

                                <!-- Password Section -->
                                <div class="profile-section-label mt-2">Change Password</div>
                                <div class="profile-section-hint">Leave blank to keep your current password</div>
                                <v-text-field
                                    label="New Password"
                                    outlined
                                    dense
                                    type="password"
                                    prepend-inner-icon="mdi-lock-outline"
                                    v-model="newPassword"
                                    class="profile-field mt-2"
                                    color="#005D6C"
                                ></v-text-field>
                                <v-text-field
                                    label="Confirm New Password"
                                    outlined
                                    dense
                                    type="password"
                                    prepend-inner-icon="mdi-lock-check-outline"
                                    v-model="confirmPassword"
                                    :rules="[passwordConfirmationRule]"
                                    class="profile-field"
                                    color="#005D6C"
                                ></v-text-field>
                            </v-form>
                        </v-card-text>

                        <!-- Actions -->
                        <v-card-actions class="profile-dialog-actions" style="background:#fff">
                            <v-btn text class="profile-cancel-btn" @click="dialog = false">Cancel</v-btn>
                            <v-btn depressed class="profile-save-btn" @click="updateUser" :loading="updating" v-action-process>
                                <v-icon left small>mdi-check</v-icon>
                                Save Changes
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-row>
        </v-main>
        <!-- Global floating AI Chatbot button/window -->
        <!-- <AIchatbot /> -->

        <!-- DeepAssist popup widget (Assistant + Support tabs) mounts here via JS -->
    </div>
</template>

<script>
import api from '@/services/api';
import { initDeepAssistWidget, destroyDeepAssistWidget } from '@/deepassist-widget';
// import AIchatbot from './AIchatbot.vue'
export default {
    // components: { AIchatbot },
    data: () => ({
        drawer: true,
        profileMenuOpen: false,
        dialog: false,
        name: '',
        email: '',
        isDev: false,
        devUsers: [],
        isDarkMode: false,
        req: [(v) => !!v || 'Required'],
        newPassword: '',
        confirmPassword: '',
        updating: false,
        orgBranding: { name: '', logo: '' },
        hints: [
            'Fetching your data…',
            'Setting things up…',
            'Almost there…',
            'Just a moment…',
            'Polishing the pixels…',
        ],
        hintIndex: 0,
        hintTimer: null,
        items: [
      // {
      //   title: 'Home',
      //   icon: 'mdi-home',
      //   to: '/',
      //   name: 'Home',
      // },
      {
        title: 'Dashboard',
        icon: 'mdi-view-dashboard',
        items: [
          { title: 'Overview', to: '/', name: 'DashboardOverview' },
          // { title: 'Project Summary', to: '/dashboard/project-summary', name: 'DashboardProjectSummary' },
          // { title: 'Quick Actions', to: '/dashboard/quick-actions', name: 'DashboardQuickActions' },
        ],
      },
      {
        title: 'Projects',
        icon: 'mdi-briefcase-variant',
        items: [
          { title: 'Project List', to: '/projects', name: 'ProjectList' },
        ],
      },
      {
        title: 'Budget Management',
        icon: 'mdi-cash-multiple',
        items: [
          { title: 'Budget List', to: '/budgets', name: 'BudgetList' },
          { title: 'Budget Overview', to: '/budget-overview', name: 'BudgetOverview' },
        ],
      },
      {
        title: 'Man Power Management',
        icon: 'mdi-account-tie',
        items: [
          { title: 'Category', to: '/category', name: 'Category' },
          { title: 'Designation', to: '/designation', name: 'Designation' },
          { title: 'Human Resource', to: '/human-resource', name: 'HumanResource' },
          { title: 'Time Sheet', to: '/time-sheet', name: 'TimeSheet' },
        ],
      },
      {
        title: 'Procurement',
        icon: 'mdi-cart-outline',
        items: [
          { title: 'Purchase Requests', to: '/procurement/requests', name: 'PurchaseRequestList' },
          { title: 'Purchase Orders', to: '/procurement/orders', name: 'PurchaseOrderList' },
          // Delivery Orders (GRN) is deferred until the DO upload integration is built. Route still
          // exists (see router/index.js) but is intentionally unlinked from the nav. See task list.
          // { title: 'Delivery Orders (GRN)', to: '/procurement/delivery-orders', name: 'DeliveryOrderList' },
          { title: 'Invoices', to: '/procurement/invoices', name: 'VendorInvoiceList' },
          { title: 'Request Payment', to: '/procurement/request-payment', name: 'RequestPayment' },
          { title: 'Payments', to: '/procurement/payments', name: 'PaymentList' },
          { title: 'Reports', to: '/procurement/reports', name: 'ProcurementReports' },
        ],
      },
      {
        title: 'Claims',
        icon: 'mdi-receipt',
        items: [
          { title: 'Claims', to: '/claims', name: 'Claims' },
        ],
      },
      {
        title: 'Reports',
        icon: 'mdi-chart-bar',
        to: '/reports',
        name: 'Reports',
      },
      {
        title: 'Settings',
        icon: 'mdi-cog-outline',
        name: 'Settings',
        items: [
          // { title: 'User Management', to: '/settings/users', name: 'UserManagement' },
          { title: 'Customer Management', to: '/settings/customers', name: 'CustomerManagement' },
          { title: 'Approval Thresholds', to: '/settings/thresholds', name: 'ApprovalThresholds' },
          //{ title: 'System Settings', to: '/settings/system', name: 'SystemSettings' },
          { title: 'Configurations', to: '/settings/configurations', name: 'Configurations' },
          // { title: 'Access Control', to: '/settings/access-control', name: 'AccessControl' },
          { title: 'Budget Templates', to: '/settings/budget-templates', name: 'BudgetTemplates' },
          // { title: 'Version', to: '/settings/version', name: 'Version' },
        ],
      },
    ],
    }),
    methods: {
        goToDashboard() {
            const DEEPCOREX_URL = process.env.VUE_APP_DEEPCOREX_URL || 'http://localhost:5101';
            const token = localStorage.getItem('access_token');
            if (token) {
                window.location.href = `${DEEPCOREX_URL}/auth/sso?token=${encodeURIComponent(token)}`;
            } else {
                window.location.href = DEEPCOREX_URL;
            }
        },
        // ── Dev-only user switcher (calls the CCoreX backend dev endpoints) ──
        devApiBase() {
            return process.env.VUE_APP_DEEPCOREX_API_URL || 'http://localhost:3001/api';
        },
        isDevOrStaging() {
            const hostname = window.location.hostname;
            return hostname === 'localhost' ||
                   hostname === '127.0.0.1' ||
                   hostname.startsWith('stg-');
        },
        roleSummary(u) {
            // When filtered by app, role is a single object, not an array
            if (u.role && u.role.role) {
                return u.role.role;
            }
            // Fallback for backward compatibility
            const roles = (u.roles || []).filter(r => r.role).map(r => `${r.app}:${r.role}`);
            return roles.length ? roles.join(', ') : 'no roles';
        },
        async loadDevUsers() {
            try {
                // Get organizationId from JWT token to ensure it's available
                const token = localStorage.getItem('access_token');
                let orgId = '';
                if (token) {
                    try {
                        const base64Url = token.split('.')[1];
                        const base64 = (base64Url.replace(/-/g, '+').replace(/_/g, '/') + '===').slice(0, Math.ceil(base64Url.length / 4) * 4);
                        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                        }).join(''));
                        const decoded = JSON.parse(jsonPayload);
                        orgId = decoded.tenantId || decoded.currentClient || decoded.organizationId || '';
                    } catch (e) {
                        console.warn('[Dev] could not decode token for orgId:', e);
                    }
                }
                // Fallback to user.organizationId if token decode fails
                if (!orgId && this.user && this.user.organizationId) {
                    orgId = this.user.organizationId;
                }
                const url = `${this.devApiBase()}/auth/dev-users?app=deeptrack${orgId ? `&organizationId=${orgId}` : ''}`;
                const res = await fetch(url);
                const data = await res.json();
                this.devUsers = data.users || [];
            } catch (e) {
                console.warn('[Dev] could not load dev users:', e.message || e);
            }
        },
        async switchUser(email) {
            if (!email || (this.user && email === this.user.email)) return;
            try {
                const res = await fetch(`${this.devApiBase()}/auth/dev-login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email }),
                });
                const data = await res.json();
                if (data.token) {
                    localStorage.setItem('access_token', data.token);
                    // Use reload instead of href to avoid Vue Router navigation conflicts
                    window.location.reload();
                } else {
                    console.error('[Dev] switch user: no token returned', data);
                }
            } catch (e) {
                console.error('[Dev] switch user failed:', e.message || e);
            }
        },
        passwordConfirmationRule() {
            return this.newPassword === this.confirmPassword || 'Passwords must match';
        },
        canAccess(routeName) {
            const permissions = this.$store.getters.rolePermissions;
            if (!this.user) return false;
            if (permissions === null) return false;
            const allowedRoles = permissions[routeName];
            if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) return false;
            return allowedRoles.includes(this.user.role);
        },
        canAccessGroup(item) {
            if (!item.items) return this.canAccess(item.name);
            // Show group if any child is accessible
            return item.items.some(child => this.canAccess(child.name));
        },
        async updateUser() {
            if (!this.$refs.profileForm.validate()) {
                return;
            }

            this.updating = true;
            try {
                // Update name if changed
                if (this.name !== this.user.name) {
                    const data = await api.data.update('users', this.user.id, { name: this.name });
                    this.$store.commit('SET_USER', data);
                    this.setUser();
                }

                // Update password if provided
                if (this.newPassword) {
                    await api.functions.invoke('update-password', {
                        newPassword: this.newPassword,
                    });

                    this.$store.dispatch('createAlert', { type: 'success', message: 'Password updated successfully. You will be logged out.' });
                    this.dialog = false;
                    this.newPassword = '';
                    this.confirmPassword = '';
                    setTimeout(() => {
                        this.logout();
                    }, 1500);
                } else {
                    this.$store.dispatch('createAlert', { type: 'success', message: 'Profile updated successfully' });
                    this.dialog = false;
                }

            } catch (error) {
                this.$store.dispatch('createAlert', { type: 'error', message: error.message });
            } finally {
                this.updating = false;
            }
        },

        async logout() {
            await api.auth.logout();
            this.$router.push('/login').catch(err => {
                if (err.name !== 'NavigationDuplicated') {
                    throw err;
                }
            });
        },
        setUser () {
            this.name = this.user.name
            this.email = this.user.email
        },
        toggleTheme() {
            this.isDarkMode = !this.isDarkMode;
            this.$vuetify.theme.dark = this.isDarkMode;
            localStorage.setItem('theme', this.isDarkMode ? 'dark' : 'light');
        },
        async fetchOrgBranding() {
            const token = localStorage.getItem('access_token') || localStorage.getItem('app-token') || localStorage.getItem('token');
            if (!token) return;
            try {
                const deepcorexUrl = process.env.VUE_APP_DEEPCOREX_URL || '';
                let base;
                let corexOrigin;
                try {
                    const u = new URL(deepcorexUrl);
                    const apiHost = u.host.replace(/(^|[.-])dash\./, '$1api-dash.');
                    base = `${u.protocol}//${apiHost}/api`;
                    corexOrigin = `${u.protocol}//${apiHost}`;
                } catch (e) {
                    base = 'http://localhost:3001/api';
                    corexOrigin = 'http://localhost:3001';
                }
                const resp = await fetch(`${base}/organization`, {
                    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
                });
                if (!resp.ok) return;
                const raw = await resp.json();
                const org = Array.isArray(raw) ? raw[0] : raw;
                if (!org) return;
                let logo = org.logoAttachment || '';
                // Make relative logo paths absolute using the CoreX backend origin
                if (logo && !logo.startsWith('data:') && !logo.startsWith('http')) {
                    logo = `${corexOrigin}${logo.startsWith('/') ? logo : `/uploads/logos/${logo}`}`;
                }
                this.orgBranding = { name: org.name || '', logo };

                // Sync organization data to DeepTrack backend
                try {
                    // Extract tenantId from JWT token
                    let tenantId = '';
                    try {
                        const base64Url = token.split('.')[1];
                        const base64 = (base64Url.replace(/-/g, '+').replace(/_/g, '/') + '===').slice(0, Math.ceil(base64Url.length / 4) * 4);
                        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                        }).join(''));
                        const decoded = JSON.parse(jsonPayload);
                        tenantId = decoded.tenantId || decoded.currentClient || decoded.organizationId || '';
                    } catch (e) {
                        // Silent fail
                    }

                    if (tenantId) {
                        await api.post('/organizations/sync', {
                            name: org.name || '',
                            logo_url: logo || '',
                            email: org.email || '',
                            phone: org.phone || '',
                            address: org.address || ''
                        });
                    }
                } catch (syncError) {
                    // Don't break UI if sync fails
                }
            } catch (e) {
                // silent
            }
        },
        restoreSidebarState() {
            const savedState = localStorage.getItem('sidebarState');
            if (savedState) {
                const state = JSON.parse(savedState);
                this.items.forEach(item => {
                    if (item.title && state[item.title] !== undefined) {
                        this.$set(item, 'active', state[item.title]);
                    }
                });
            }
        },
        saveSidebarState() {
            const state = {};
            this.items.forEach(item => {
                if (item.title) {
                    state[item.title] = item.active || false;
                }
            });
            localStorage.setItem('sidebarState', JSON.stringify(state));
        }
    },
    watch: {
        items: {
            handler() {
                this.saveSidebarState();
            },
            deep: true
        },
        drawer(newVal) {
            localStorage.setItem('drawerOpen', newVal);
        }
    },
    computed: {
        alert () {
            return this.$store.getters.loadedAlert
        },
        user () {
            return this.$store.getters.loadedUser
        },
        userLoading () {
            return this.$store.getters.loadedUserLoading
        },
        pageLoading () {
            return this.$store.getters.loadedPageLoading
        }
    },
    mounted () {
        this.setUser();
        this.isDarkMode = false;
        this.$vuetify.theme.dark = false;
        localStorage.setItem('theme', 'light');
        this.isDev = this.isDevOrStaging();
        if (this.isDev) this.loadDevUsers();
        this.fetchOrgBranding();
        initDeepAssistWidget({
            deepassistUrl: process.env.VUE_APP_DEEPASSIST_URL || 'http://localhost:5109',
            appLabel: 'DeepTrack',
            support: {
                apiBase: process.env.VUE_APP_API_BASE_URL || 'http://localhost:3003/api',
                app: 'DeepTrack',
            },
        });
    },
    beforeDestroy () {
        destroyDeepAssistWidget();
    },
};
</script>

<style scoped>
/* ── App bar ──────────────────────────────────────────────── */
.app-bar-title {
  font-size: 16px !important;
  font-weight: 700 !important;
  letter-spacing: -0.01em !important;
}

.header-back-btn {
  border-color: rgba(255,255,255,0.35) !important;
  color: rgba(255,255,255,0.85) !important;
  font-size: 12px !important;
}

.header-back-btn:hover {
  background: rgba(255,255,255,0.12) !important;
  color: #fff !important;
}

/* User avatar in header */
.user-avatar-btn {
  background: rgba(255,255,255,0.18) !important;
  cursor: pointer;
}

.user-avatar-initial {
  color: #fff !important;
  font-weight: 700;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
}

/* ── Sidebar profile section ────────────────────────────────── */
.sidebar-profile-trigger {
  display: flex;
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.sidebar-profile-trigger:hover {
  background-color: #F4F6F8;
}

.sidebar-profile-avatar {
  background: #005D6C !important;
  flex-shrink: 0;
}

.sidebar-profile-initial {
  color: #fff !important;
  font-weight: 700;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
}

.sidebar-profile-info {
  flex: 1;
  overflow: hidden;
}

.sidebar-profile-name {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #1A2332 !important;
  font-family: 'Inter', sans-serif !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.sidebar-profile-role {
  font-size: 11px !important;
  color: #64748B !important;
  font-family: 'Inter', sans-serif !important;
  line-height: 1.2;
}

.sidebar-profile-chevron {
  color: #64748B !important;
  transition: transform 0.2s ease !important;
}

.sidebar-profile-chevron.rotated {
  transform: rotate(180deg) !important;
}

/* Profile dropdown menu */
.profile-dropdown-list {
  border: 1px solid #E4E7EC !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10) !important;
}

.profile-dropdown-header {
  opacity: 1 !important;
}

.profile-dropdown-name {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #1A2332 !important;
  font-family: 'Inter', sans-serif !important;
}

.profile-dropdown-email {
  font-size: 11px !important;
  color: #64748B !important;
  font-family: 'Inter', sans-serif !important;
}

.profile-dropdown-item {
  border-radius: 6px !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 13px !important;
}

.profile-dropdown-item:hover {
  background-color: #F4F6F8 !important;
}

.profile-signout-item:hover {
  background-color: #FEF2F2 !important;
}

/* ── Sidebar org branding ─────────────────────────────────── */
.org-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 72px;
  padding: 12px 16px;
  border-bottom: 1px solid #E4E7EC;
  background: #fff;
  flex-shrink: 0;
}

.org-brand-logo-wrap {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 8px;
  border: 1px solid #E4E7EC;
  background: #fff;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.org-brand-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.org-brand-initial {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #005D6C;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
}

.org-brand-name {
  font-size: 13px;
  font-weight: 600;
  color: #1A2332;
  font-family: 'Inter', sans-serif;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Dev switcher ─────────────────────────────────────────── */
.dt-dev-switcher {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px dashed rgba(255, 255, 255, 0.5);
  border-radius: 6px;
  padding: 2px 8px;
}
.dt-dev-switcher-label {
  font-size: 12px;
  white-space: nowrap;
  color: #fff;
}
.dt-dev-switcher-select {
  background: #fff;
  color: #252525;
  border: none;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 12px;
  max-width: 260px;
  cursor: pointer;
}

.global-loader {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  background:
    radial-gradient(ellipse at 20% 0%, rgba(20, 184, 166, 0.08), transparent 60%),
    radial-gradient(ellipse at 80% 100%, rgba(99, 102, 241, 0.08), transparent 60%);
  backdrop-filter: blur(5px) saturate(120%);
  -webkit-backdrop-filter: blur(5px) saturate(120%);
  animation: cx-fade-in 0.25s ease-out;
}
.cx-loader-card {
  position: relative;
  min-width: 280px;
  max-width: 92vw;
  padding: 22px 28px 20px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  box-shadow:
    0 16px 40px -16px rgba(0, 60, 80, 0.18),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset,
    0 0 0 1px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}
.cx-loader-pulse { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; }
.cx-loader-pulse-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: linear-gradient(135deg, #14b8a6, #6366f1);
  box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.45);
  animation: cx-pulse 1.8s ease-in-out infinite;
}
@keyframes cx-pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.45), 0 0 0 0 rgba(99, 102, 241, 0.30); }
  50% { transform: scale(1.15); box-shadow: 0 0 0 12px rgba(20, 184, 166, 0), 0 0 0 22px rgba(99, 102, 241, 0); }
}
.cx-loader-title { font-size: 15px; font-weight: 600; margin: 0; color: #0f172a; letter-spacing: -0.01em; }
.cx-loader-hint { font-size: 12.5px; color: #64748b; margin: 0; min-height: 16px; animation: cx-fade-hint 2.2s ease-in-out infinite; }
@keyframes cx-fade-hint { 0%, 100% { opacity: 0.5; transform: translateY(2px); } 50% { opacity: 1; transform: translateY(0); } }
.cx-loader-progress { width: 100%; height: 3px; border-radius: 3px; background: rgba(20, 184, 166, 0.10); overflow: hidden; margin-top: 2px; }
.cx-loader-progress-bar { height: 100%; width: 35%; background: linear-gradient(90deg, #14b8a6, #6366f1, #ec4899); border-radius: 3px; animation: cx-progress 1.6s ease-in-out infinite; }
@keyframes cx-progress { 0% { margin-left: -35%; } 50% { margin-left: 50%; width: 50%; } 100% { margin-left: 105%; width: 35%; } }
@keyframes cx-fade-in { from { opacity: 0; } to { opacity: 1; } }

.loader-content {
  text-align: center;
  color: #1976D2;
}

.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.wrapper {
  width: 200px;
  height: 60px;
  position: relative;
  z-index: 1;
}

.circle {
  width: 20px;
  height: 20px;
  position: absolute;
  border-radius: 50%;
  background-color: #1976D2;
  left: 15%;
  transform-origin: 50%;
  animation: circle7124 .5s alternate infinite ease;
}

@keyframes circle7124 {
  0% {
    top: 60px;
    height: 5px;
    border-radius: 50px 50px 25px 25px;
    transform: scaleX(1.7);
  }

  40% {
    height: 20px;
    border-radius: 50%;
    transform: scaleX(1);
  }

  100% {
    top: 0%;
  }
}

.circle:nth-child(2) {
  left: 45%;
  animation-delay: .2s;
}

.circle:nth-child(3) {
  left: auto;
  right: 15%;
  animation-delay: .3s;
}

.shadow {
  width: 20px;
  height: 4px;
  border-radius: 50%;
  background-color: rgba(0,0,0,0.2);
  position: absolute;
  top: 62px;
  transform-origin: 50%;
  z-index: -1;
  left: 15%;
  filter: blur(1px);
  animation: shadow046 .5s alternate infinite ease;
}

@keyframes shadow046 {
  0% {
    transform: scaleX(1.5);
  }

  40% {
    transform: scaleX(1);
    opacity: .7;
  }

  100% {
    transform: scaleX(.2);
    opacity: .4;
  }
}

.shadow:nth-child(4) {
  left: 45%;
  animation-delay: .2s
}

.shadow:nth-child(5) {
  left: auto;
  right: 15%;
  animation-delay: .3s;
}

/* ── Profile Dialog ──────────────────────────────────────── */
.profile-dialog-header {
  background: #005D6C;
  padding: 20px 20px 20px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
}

.profile-dialog-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255,255,255,0.22);
  border: 2px solid rgba(255,255,255,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  font-family: 'Inter', sans-serif;
  flex-shrink: 0;
}

.profile-dialog-header-info {
  flex: 1;
  overflow: hidden;
}

.profile-dialog-username {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-dialog-useremail {
  font-size: 12px;
  color: rgba(255,255,255,0.75);
  font-family: 'Inter', sans-serif;
  margin-top: 2px;
}

.profile-dialog-close {
  position: absolute;
  top: 12px;
  right: 12px;
}

.profile-dialog-body {
  padding: 20px 24px 8px !important;
}

.profile-section-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-family: 'Inter', sans-serif;
  margin-bottom: 2px;
}

.profile-section-hint {
  font-size: 12px;
  color: #94A3B8;
  font-family: 'Inter', sans-serif;
  margin-bottom: 2px;
}

.profile-field {
  font-family: 'Inter', sans-serif !important;
}

.profile-dialog-actions {
  padding: 8px 24px 20px !important;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid #E4E7EC;
}

.profile-cancel-btn {
  color: #64748B !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.profile-save-btn {
  background: #005D6C !important;
  color: #fff !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-radius: 6px !important;
  padding: 0 16px !important;
  height: 36px !important;
}

.profile-save-btn:hover {
  background: #007A8C !important;
}

/* ── App header branding ──────────────────────────────────── */
.app-branding {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: 4px;
}

.app-logo-wrap {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  padding: 4px;
}

.app-logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
